import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Heart, FileText, Users, Shield, Home, Scale, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { publicApi } from '../lib/api';
import { useLanguage } from '../hooks/useLanguage';
import SEO from '../components/SEO';

const FamilyLegalPlanningPage = () => {
  const { t } = useLanguage();

  useEffect(() => {
    publicApi.trackEvent({ event_type: 'page_view', page: 'family-legal-planning' }).catch(() => {});
  }, []);

  const estatePlanningServices = [
    "Wills tailored to your family structure",
    "Guardianship designations for minor children",
    "Medical directives and powers of attorney",
    "Trust planning (as appropriate)"
  ];

  const uncontestedDivorceFor = [
    "Couples who agree on property division",
    "Situations without custody disputes or ongoing conflict"
  ];

  const adoptionServices = [
    "Stepparent and adult adoptions",
    "Legal name changes"
  ];

  const strategySessionIncludes = [
    "Talk through your situation",
    "Identify your options",
    "Outline practical next steps",
    "Help you avoid common missteps"
  ];

  return (
    <div data-testid="family-legal-planning-page">
      <SEO 
        title="Family & Personal Legal Planning"
        description="Thoughtful legal guidance for life's most personal decisions. Estate planning, prenuptial agreements, uncontested divorce, adoptions, and family law strategy sessions."
      />
      
      {/* Hero Section */}
      <section className="bg-[#FDFCFA] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-6">
                Practice Area
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight leading-[1.1] text-[#222222] mb-6">
                Family & Personal Legal Planning
              </h1>
              <p className="text-xl text-[#555555] leading-relaxed mb-8">
                Thoughtful legal guidance for life's most personal decisions
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://app.practicepanther.com/IntakeForm/Create?templateGuid=practice-area-deploy" target="_blank" rel="noopener noreferrer" data-testid="family-cta-get-started">
                  <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-8 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <Link to="/book" data-testid="family-cta-book">
                  <Button className="bg-[#1F2A44] hover:bg-[#151D30] text-white px-8 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                    Book a Consultation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-[#C2A46D]/30 rounded-lg"></div>
              <img
                src="https://images.pexels.com/photos/7414960/pexels-photo-7414960.jpeg"
                alt="Family planning their future together"
                className="w-full h-auto object-cover rounded-lg shadow-2xl relative z-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <Heart className="w-12 h-12 text-[#C2A46D] mx-auto mb-6" />
          <p className="text-lg text-[#555555] leading-relaxed mb-6">
            At J Brantley Law, we approach family-related legal services with care, clarity, and intention. Not every family matter has to be contentious or overwhelming. Many of the most important decisions you will make for yourself and your loved ones can be handled proactively, strategically, and with confidence.
          </p>
          <p className="text-lg text-[#555555] leading-relaxed">
            We focus on forward-looking, low-conflict legal services that help you protect what matters most—your family, your assets, and your peace of mind.
          </p>
        </div>
      </section>

      {/* Estate Planning Section */}
      <section className="bg-[#F5F3EF] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-8 h-8 text-[#1F2A44]" />
                <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D]">
                  Protect Your Loved Ones
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-6">
                Estate Planning for Families
              </h2>
              <p className="text-[#555555] leading-relaxed mb-4">
                If you have children, own a business, or simply want to make things easier for your family in the future, a well-crafted estate plan is essential.
              </p>
              <p className="text-[#555555] leading-relaxed mb-6">
                We help you put clear instructions in place so your loved ones are protected and supported, without confusion or unnecessary court involvement.
              </p>
              <div className="space-y-3 mb-6">
                {estatePlanningServices.map((service, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#1F2A44] flex-shrink-0 mt-0.5" />
                    <span className="text-[#555555]">{service}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white p-4 border-l-4 border-[#C2A46D]">
                <p className="text-sm text-[#555555] italic">
                  This is not just about documents. It is about creating a plan that gives you confidence today and clarity for your family tomorrow.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/7641917/pexels-photo-7641917.jpeg"
                alt="Family reviewing estate planning documents"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Prenuptial Agreements Section */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.pexels.com/photos/3094345/pexels-photo-3094345.jpeg"
                alt="Couple planning their future"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-8 h-8 text-[#1F2A44]" />
                <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D]">
                  Start With Clarity
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-6">
                Prenuptial & Postnuptial Agreements
              </h2>
              <p className="text-[#555555] leading-relaxed mb-4">
                Having honest conversations about finances before or during marriage is one of the most practical decisions you can make.
              </p>
              <p className="text-[#555555] leading-relaxed mb-6">
                We draft and review prenuptial and postnuptial agreements that are clear, enforceable, and tailored to your specific circumstances—whether you are protecting a business, prior assets, or future earnings.
              </p>
              <div className="bg-[#F5F3EF] p-4 rounded">
                <p className="text-sm text-[#555555]">
                  <strong>Our approach is thoughtful and balanced</strong>, helping both parties move forward with confidence and mutual understanding.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Uncontested Divorce Section */}
      <section className="bg-[#1F2A44]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="text-center mb-12">
            <Scale className="w-10 h-10 text-[#C2A46D] mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-4">
              Uncontested Divorce Services
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              A streamlined approach for amicable transitions
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <p className="text-white/80 text-center mb-6">
              Not every divorce has to be a battle. If you and your spouse are aligned on key issues, we offer guidance and document preparation for uncontested divorces, helping you move through the process efficiently and with dignity.
            </p>
            <div className="bg-white/10 p-6 rounded-lg mb-6">
              <p className="text-[#C2A46D] font-semibold mb-3">This service is appropriate for:</p>
              <div className="space-y-2">
                {uncontestedDivorceFor.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#C2A46D] flex-shrink-0 mt-0.5" />
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-white/70 text-center text-sm">
              We focus on keeping the process clear, respectful, and as straightforward as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Adoptions & Name Changes Section */}
      <section className="bg-[#FDFCFA] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-8 h-8 text-[#1F2A44]" />
                <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D]">
                  Meaningful Transitions
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-6">
                Adoptions & Name Changes
              </h2>
              <p className="text-[#555555] leading-relaxed mb-6">
                Some legal moments are deeply personal and worth celebrating. We assist with:
              </p>
              <div className="space-y-3 mb-6">
                {adoptionServices.map((service, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#1F2A44] flex-shrink-0 mt-0.5" />
                    <span className="text-[#555555]">{service}</span>
                  </div>
                ))}
              </div>
              <p className="text-[#555555] leading-relaxed">
                These services are handled with care and attention to detail, ensuring the process is smooth from start to finish.
              </p>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/7579353/pexels-photo-7579353.jpeg"
                alt="Happy family celebrating adoption"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Strategy Sessions Section */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Users className="w-10 h-10 text-[#1F2A44] mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-4">
              Family Law Strategy Sessions
            </h2>
            <p className="text-[#555555]">
              Clarity before you take your next step
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <p className="text-[#555555] text-center mb-8">
              If you are navigating a family-related legal issue and are not sure where to begin, a strategy session can provide the guidance you need.
            </p>
            <div className="bg-[#F5F3EF] p-8 rounded-lg">
              <p className="font-semibold text-[#222222] mb-4">In this focused consultation, we will:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {strategySessionIncludes.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#1F2A44] rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#555555]">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-[#777777] mt-6 text-center">
                This is a valuable option if you want experienced legal insight without committing to full representation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Thoughtful Approach Section */}
      <section className="bg-[#F5F3EF] border-b border-[#E8E4DC]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="text-2xl md:text-3xl font-serif text-[#222222] mb-6">
            A Thoughtful Approach to Family Law
          </h2>
          <p className="text-[#555555] leading-relaxed mb-4">
            We intentionally focus on low-conflict, solutions-oriented services. If your matter becomes contested or requires litigation, we are happy to connect you with trusted attorneys who can support you in that next phase.
          </p>
          <p className="text-[#555555] leading-relaxed">
            Our goal is to provide legal services that are not only effective, but also sustainable—for you and for our clients.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1F2A44]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <Home className="w-12 h-12 text-[#C2A46D] mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">
            Let's Create a Plan That Works for You
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Whether you are planning for the future, entering a new chapter, or navigating a transition, you do not have to figure it out alone. Schedule a consultation today to get clear, practical guidance tailored to your situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://app.practicepanther.com/IntakeForm/Create?templateGuid=practice-area-deploy" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <Link to="/book">
              <Button className="bg-white text-[#1F2A44] hover:bg-[#FDFCFA] px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                Schedule a Consultation
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FamilyLegalPlanningPage;
